import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { Article } from '../../types'
import api from '../../services/api'
import Loading from '../../components/common/Loading'
import ErrorMessage from '../../components/common/ErrorMessage'
import { formatDate, getImageUrl } from '../../utils/helpers'
import { usePageMeta } from '../../hooks/usePageMeta'
import './News.css'

function NewsDetail() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const [article, setArticle] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const coverImage = article?.cover_media?.file_path
    ? getImageUrl(article.cover_media.file_path)
    : undefined

  usePageMeta({
    title: article?.title || 'Article',
    description: article?.excerpt || undefined,
    ogImage: coverImage,
  })

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return

      try {
        setLoading(true)
        setError(null)
        const data = await api.get<Article>(`/articles/${slug}`)
        setArticle(data)
      } catch (err) {
        if (err instanceof Error && err.message.includes('404')) {
          setError('Article non trouvé')
        } else {
          setError(err instanceof Error ? err.message : 'Erreur lors du chargement de l\'article')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchArticle()
  }, [slug])

  const handleShare = (platform: 'facebook' | 'twitter' | 'linkedin' | 'whatsapp' | 'native') => {
    if (!article) return

    const url = encodeURIComponent(window.location.href)
    const title = encodeURIComponent(article.title)

    if (platform === 'native' && navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt || article.title,
        url: window.location.href,
      }).catch(() => {})
      return
    }

    let shareUrl = ''
    if (platform === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`
    } else if (platform === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`
    } else if (platform === 'linkedin') {
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
    } else if (platform === 'whatsapp') {
      shareUrl = `https://wa.me/?text=${title}%20${url}`
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400')
    }
  }

  if (loading) return <Loading />

  if (error) {
    return (
      <div className="container">
        <ErrorMessage
          message={error}
          onRetry={() => navigate('/actualites')}
        />
      </div>
    )
  }

  if (!article) return null

  const authorName = article.author
    ? `${article.author.first_name} ${article.author.last_name}`
    : null

  return (
    <div className="news-detail-page">
      <div className="container">
        <nav className="breadcrumb" aria-label="Fil d'ariane">
          <Link to="/">Accueil</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to="/actualites">Actualités</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{article.title}</span>
        </nav>

        <article className="article-detail">
          <header className="article-header">
            <h1 className="article-title">{article.title}</h1>

            <div className="article-meta">
              <time className="article-date" dateTime={article.published_at}>
                {formatDate(article.published_at)}
              </time>
              {authorName && (
                <>
                  <span className="separator">•</span>
                  <span className="article-author">Par {authorName}</span>
                </>
              )}
            </div>

            {article.excerpt && (
              <p className="article-excerpt">{article.excerpt}</p>
            )}
          </header>

          {coverImage && typeof coverImage === 'string' && (
            <div className="article-cover">
              <img src={coverImage} alt={article.title} />
            </div>
          )}

          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          />

          <footer className="article-footer">
            <div className="share-buttons">
              <span className="share-label">Partager :</span>
              <button
                onClick={() => handleShare('facebook')}
                className="share-button share-facebook"
                aria-label="Partager sur Facebook"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Facebook
              </button>
              <button
                onClick={() => handleShare('twitter')}
                className="share-button share-twitter"
                aria-label="Partager sur Twitter"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
                Twitter
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="share-button share-linkedin"
                aria-label="Partager sur LinkedIn"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                LinkedIn
              </button>
              <button
                onClick={() => handleShare('whatsapp')}
                className="share-button share-whatsapp"
                aria-label="Partager sur WhatsApp"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp
              </button>
              {typeof navigator !== 'undefined' && 'share' in navigator && (
                <button
                  onClick={() => handleShare('native')}
                  className="share-button share-native"
                  aria-label="Partager"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  Partager
                </button>
              )}
            </div>

            <Link to="/actualites" className="back-to-list">
              ← Retour aux actualités
            </Link>
          </footer>
        </article>
      </div>
    </div>
  )
}

export default NewsDetail
